import React, {
  useState,
  useEffect,
  InputHTMLAttributes,
  ClipboardEventHandler,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler, MouseEventHandler
} from 'react';
import _ from 'lodash';
import {useIntl} from 'react-intl';
import {mergeClassnames} from '../../../utils';
import cross from '../../../assets/cross.svg';
import full from './InputEmailChips.module.scss';
import styles from './Chip.module.scss';

type InputVariant = 'full';
type ComponentCustomClassnames = {
  container: string,
  filledInputContainer: string,
  label: string,
  inputContainer: string,
  input: string,
  errorContainer: string,
  error: string,
  clipContainer: string,
};

export interface InputComponentProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
  id: string,
  label?: React.ReactNode,
  value?: string,
  error?: string,
  onChange: (value: string) => void,
  showErrorOnSubmit?: boolean
}

export interface StylesInputComponentProps extends InputComponentProps {
  customClassnames: ComponentCustomClassnames;
}

export interface InputEmailChipsProps extends InputComponentProps {
  variant?: InputVariant,
  customClassnames?: Partial<ComponentCustomClassnames>;
}

interface ChipProps extends InputHTMLAttributes<HTMLInputElement> {
  email: string;
  onDelete: MouseEventHandler
}

const Chip = ({email, onDelete}: ChipProps) => {
  return (
    <div className={styles.chip}>
      <div className={styles.email}>{email}</div>
      <img className={styles.removeIcon} src={cross} alt="remove" onClick={onDelete}/>
    </div>
  );
};

const InputEmailChipsComponent = ({
                                    id,
                                    label,
                                    value: outsideValue,
                                    error: outsideError,
                                    customClassnames,
                                    placeholder,
                                    onChange,
                                    showErrorOnSubmit,
                                    ...rest
                                  }: StylesInputComponentProps) => {
  const intl = useIntl();
  const [items, setItems] = useState<string[]>(
    outsideValue
      ? [...outsideValue?.split(/[,\s]/)]
      : []
  );
  const [value, setValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);

  const hasError = error.length > 0 || Boolean(outsideError);
  const showErrorCondition = hasError && (showErrorOnSubmit || (touched && !value));

  const emailRegex = /[\w.-]+@[\w.-]+\.[\w.-]+/;

  const isInList = (email: string, items: string[]) => {
    return items.includes(email);
  };

  const isEmail = (email: string) => {
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (onChange) {
      onChange(items.join(' '));
    }
  }, [items]);

  useEffect(() => {
    setValue('');
    setError('');
    setTouched(false);
    updateItems(outsideValue || '', 'replace');
  }, [outsideValue]);

  const isValid = (email: string, items: string[]) => {
    const errors = [];

    if (isInList(email, items)) {
      const message = intl.formatMessage({
        id: 'input-email-error-already-added-email',
        defaultMessage: 'already added',
      });
      errors.push(`${email} ${message}`);
    }

    if (email.length > 0 && !isEmail(email)) {
      const message = intl.formatMessage({
        id: 'input-email-error-not-valid-email',
        defaultMessage: 'is not valid',
      });
      errors.push(`${email} ${message}`);
    }

    if (items.length > 4) {
      const message = intl.formatMessage({
        id: 'input-email-error-no-more-emails-required',
        defaultMessage: 'Too much emails added',
      });
      errors.push(`${message}`);
    }

    if (errors.length > 0) {
      setError(errors.join(' '));
    }

    return Boolean(email) && (errors.length === 0);
  };

  const parseEmails = (value: string, items: string[]) => {
    return value.trim().split(/[,\s]/)
      .filter(substring => isValid(substring, items))
      .map(email => email.trim());
  }

  const updateItems = (value: string, method: 'push' | 'replace' = 'push') => {
    const emails = parseEmails(value, (method === 'push') ? items : []);
    setItems(items => (method === 'push') ? [...items, ...emails] : [...emails]);

    return emails;
  }

  const updateValue = (value: string, emails: string[]) => {
    let rest = value;
    emails.forEach(email => {
      rest = rest.replace(new RegExp(`${email}[,\s]*`, 'gmi'), '');
    }, value);
    setValue(rest);
  }

  const updateItemsAndValue = (value: string) => {
    const emails = updateItems(value);
    updateValue(value, emails);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (['Enter', 'Tab', ','].includes(e.key)) {
      e.preventDefault();
      updateItemsAndValue(value);
    }
  };
  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    setError('');
    updateItemsAndValue(value);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setError('');
    setTouched(true);
    if (/[,\s]$/.test(e.target.value)) {
      updateItemsAndValue(e.target.value);
    } else {
      setValue(e.target.value);
    }
  };

  const handlePaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    setError('');
    if (e.clipboardData) {
      const paste = e.clipboardData.getData('text');
      updateItemsAndValue(paste.trim());
    }
  };

  const handleDeleteChip: (item: string) => MouseEventHandler = (item: string) => () => {
    setError('');
    setItems(items.filter(i => i !== item));
  };

  return (
    <div className={customClassnames.container}>
      <div className={customClassnames.inputContainer}>
        {(items.length > 0) && (
          <div className={customClassnames.clipContainer}>
            {items.map((item, index) => <Chip email={item} onDelete={handleDeleteChip(item)} key={index}/>)}
          </div>
        )}
        <input
          id={id}
          type="text"
          value={value}
          className={customClassnames.input}
          placeholder={placeholder}
          {...rest}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
        {label && (
          <label htmlFor={id} className={customClassnames.label}>
            {label}
          </label>
        )}
      </div>
      <div className={customClassnames.errorContainer}>
        {showErrorCondition && (
          <div className={customClassnames.error}>{error || outsideError}</div>
        )}
      </div>
    </div>
  );
};

const InputEmailChips = ({
                           variant = 'full',
                           customClassnames = {},
                           id,
                           onChange,
                           ...rest
                         }: InputEmailChipsProps) => {
  switch (variant) {
    case 'full':
    default:
      return (
        <InputEmailChipsComponent
          id={id}
          customClassnames={mergeClassnames(full, customClassnames) as ComponentCustomClassnames}
          onChange={onChange}
          {..._.omit(rest, ['onBlur'])}
        />
      );
  }
};

export default InputEmailChips;
